/**
 * src/layouts/aurora/palettes.js
 *
 * CSS-variable palettes for Aurora layouts. Applied as inline style
 * on the layout root so they override the user's selected colour theme
 * for descendants only — themes still drive Beta UI.
 *
 * Both follow the 70 / 20 / 10 rule: neutral surfaces, contrast text,
 * indigo accent reserved for active nav, primary CTA, and focus.
 */

export const AURORA_DARK_VARS = {
  /* 70% — surfaces (deep navy, very low chroma) */
  '--bg': '#0d1124',
  '--bg2': '#131736',
  '--bg3': '#1a1e3f',
  '--bg4': '#242a52',
  /* Chrome layers — three distinct shades (topbar darkest, sidebar middle,
     content = --bg lightest) so the regions read without separator lines. */
  '--bg-topbar': '#04061a',
  '--bg-sidebar': '#080a20',
  /* 20% — contrast (white + cool greys, hairlines minimal) */
  '--fg': '#ffffff',
  '--fg2': '#a6acca',
  '--fg3': '#6f76a1',
  '--fg4': '#555b85' /* quaternary — more muted than fg3 (disabled/placeholder text) */,
  '--border': 'rgba(255,255,255,0.04)',
  '--border2': 'rgba(255,255,255,0.08)',
  /* 10% — accent (indigo) */
  '--accent': '#7c83ff',
  '--accent2': '#6166e8',
  '--accent-ink': '#ffffff',
  '--accent-bg': 'rgba(124,131,255,0.10)',
  '--accent-border': 'rgba(124,131,255,0.22)',
  '--accent-border2': 'rgba(124,131,255,0.14)',
  '--accent-border3': 'rgba(124,131,255,0.28)',
  '--accent-border4': 'rgba(124,131,255,0.32)',
  '--accent-subtle': 'rgba(124,131,255,0.05)',
  /* Semantic */
  '--ok': '#4ade80',
  '--ok-bg': 'rgba(74,222,128,0.10)',
  '--warn': '#f4b266',
  '--warn-bg': 'rgba(244,178,102,0.10)',
  '--danger': '#f08a8f',
  '--danger-bg': 'rgba(240,138,143,0.10)',
  '--info': '#60a5fa',
  '--info-bg': 'rgba(96,165,250,0.10)',
  /* Severity ramp — see the note above THEMES in src/state/useTheme.js */
  '--critical': '#ef4444',
  '--critical-bg': 'rgba(239,68,68,0.10)',
  '--high': '#fb7185',
  '--high-bg': 'rgba(251,113,133,0.10)',
  '--medium': '#fb923c',
  '--medium-bg': 'rgba(251,146,60,0.10)',
  '--medium-strong': '#ea580c' /* deeper stop for a --medium gradient */,
  '--low': '#facc15',
  '--low-bg': 'rgba(250,204,21,0.10)',
  '--neutral': '#6f76a1' /* = --fg3 */,
  '--neutral-bg': 'rgba(111,118,161,0.16)',
  /* Insets */
  '--bg-inset': 'rgba(13,17,36,0.6)',
  '--bg-inset2': 'rgba(13,17,36,0.7)',
};

export const AURORA_LIGHT_VARS = {
  /* 70% — surfaces (cool near-white, very subtle separation) */
  '--bg': '#f7f8fc',
  '--bg2': '#ffffff',
  '--bg3': '#eef0f7',
  '--bg4': '#e2e6f0',
  /* Chrome layers — three distinct shades (topbar darkest, sidebar middle,
     content = --bg lightest) so the regions read without separator lines. */
  '--bg-topbar': '#dfe3ed',
  '--bg-sidebar': '#ebeef5',
  /* 20% — contrast (deep navy text + hairlines) */
  '--fg': '#0e1124',
  '--fg2': '#4b5374',
  '--fg3': '#8b91ad',
  '--fg4': '#a9afc4' /* quaternary — more muted than fg3 (disabled/placeholder text) */,
  '--border': 'rgba(14,17,36,0.06)',
  '--border2': 'rgba(14,17,36,0.10)',
  /* 10% — accent (indigo, slightly darker for AA on white) */
  '--accent': '#6366f1',
  '--accent2': '#4f52d8',
  '--accent-ink': '#ffffff',
  '--accent-bg': 'rgba(99,102,241,0.08)',
  '--accent-border': 'rgba(99,102,241,0.22)',
  '--accent-border2': 'rgba(99,102,241,0.14)',
  '--accent-border3': 'rgba(99,102,241,0.28)',
  '--accent-border4': 'rgba(99,102,241,0.32)',
  '--accent-subtle': 'rgba(99,102,241,0.05)',
  /* Semantic */
  '--ok': '#15803d',
  '--ok-bg': 'rgba(21,128,61,0.08)',
  '--warn': '#b45309',
  '--warn-bg': 'rgba(180,83,9,0.08)',
  '--danger': '#b91c1c',
  '--danger-bg': 'rgba(185,28,28,0.08)',
  '--info': '#2563eb',
  '--info-bg': 'rgba(37,99,235,0.08)',
  /* Severity ramp — darker than the dark-theme values so they still meet AA
     on the near-white surfaces */
  '--critical': '#991b1b',
  '--critical-bg': 'rgba(153,27,27,0.08)',
  '--high': '#be123c',
  '--high-bg': 'rgba(190,18,60,0.08)',
  '--medium': '#c2410c',
  '--medium-bg': 'rgba(194,65,12,0.08)',
  '--medium-strong': '#9a3412' /* deeper stop for a --medium gradient */,
  '--low': '#a16207',
  '--low-bg': 'rgba(161,98,7,0.08)',
  '--neutral': '#8b91ad' /* = --fg3 */,
  '--neutral-bg': 'rgba(139,145,173,0.14)',
  /* Insets */
  '--bg-inset': 'rgba(247,248,252,0.7)',
  '--bg-inset2': 'rgba(247,248,252,0.85)',
};
